import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  Collapse,
  Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  addContentAction,
  fetchContentAction,
  clearContentAction,
} from "../../../redux/Actions/contentActions";
import MessageCard from "../../MessageCard/MessageCard";

const Lesson = ({ lesson, teacherCourseId, onZoomLinkChange }) => {
  const dispatch = useDispatch();
  const [exerciseLink, setExerciseLink] = useState("");
  const [zoomLink, setZoomLink] = useState(lesson?.zoomLink || "");
  const [zoomEditMode, setZoomEditMode] = useState(false);

  const [message, setMessage] = useState("");
  const [deadline, setDeadline] = useState("");
  const [file, setFile] = useState(null);
  const [uploadInfo, setUploadInfo] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [expandedContentId, setExpandedContentId] = useState(null);
  const [showAddContent, setShowAddContent] = useState(true);

  const contentState = useSelector((state) => state.fetchContent);
  const contentList = contentState?.contentList || [];

  useEffect(() => {
    if (!zoomLink) {
      setZoomEditMode(true); // or false depending on your UX
    }
  }, [zoomLink]);

  useEffect(() => {
    if (onZoomLinkChange) onZoomLinkChange(zoomLink); // notify parent
  }, [zoomLink, onZoomLinkChange]);

  useEffect(() => {
    if (lesson?.id && teacherCourseId) {
      dispatch(fetchContentAction(lesson.id, teacherCourseId));
    }
  }, [lesson?.id, teacherCourseId, dispatch]);

  const handleFileChange = (e) => {
    const uploaded = e.target.files[0];
    setFile(uploaded);
    setUploadInfo(uploaded ? `Selected: ${uploaded.name}` : "");
  };

  // Calculate next order number
  const nextOrderNumber =
    contentList.length === 0
      ? 1
      : Math.max(...contentList.map((c) => c.orderNumber || 0)) + 1;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!lesson?.id || !teacherCourseId) return;

    const formData = new FormData();
    formData.append("lessonId", lesson.id);
    formData.append("link", exerciseLink);
    formData.append("orderNumber", nextOrderNumber);
    formData.append("message", message);
    formData.append("deadline", deadline);
    if (file) formData.append("file", file);

    dispatch(addContentAction(formData))
      .then(() => {
        dispatch(clearContentAction()); // ✅ Clear old content
        dispatch(fetchContentAction(lesson.id, teacherCourseId));
        setExerciseLink("");
        setMessage("");
        setDeadline("");
        setFile(null);
        setUploadInfo("");
        setFeedback({
          type: "success",
          message: "Content uploaded successfully.",
        });
      })
      .catch(() => {
        setFeedback({ type: "error", message: "Failed to upload content." });
      });
  };

  return (
    <Grid item xs={12} md={6} sx={{ maxHeight: "80vh", overflowY: "auto" }}>
      <Card className="course-form-card">
        <CardContent>
          <Typography variant="h6">Lesson #{lesson.number}</Typography>

          <TextField
            fullWidth
            label="Zoom Link"
            type="url"
            value={zoomLink}
            onChange={(e) => setZoomLink(e.target.value)}
            variant="outlined"
            margin="normal"
            disabled={!zoomEditMode}
          />

          {zoomLink && !zoomEditMode && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setZoomEditMode(true)}
              sx={{ mt: 1, height: "40px" }}
            >
              Edit
            </Button>
          )}

          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            onClick={() => setShowAddContent(!showAddContent)}
            sx={{ mb: 2 }}
          >
            {showAddContent ? "Hide Add Content" : "Add Content"}
          </Button>

          <Collapse in={showAddContent}>
            <Typography variant="h6">Add Content</Typography>

            {feedback && (
              <MessageCard
                message={feedback.message}
                type={feedback.type}
                onClose={() => setFeedback(null)}
              />
            )}

            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <TextField
                fullWidth
                label="Content Order Number"
                value={nextOrderNumber}
                disabled
                variant="outlined"
                margin="normal"
              />

              <TextField
                fullWidth
                label="Exercise Link"
                type="url"
                value={exerciseLink}
                onChange={(e) => setExerciseLink(e.target.value)}
                variant="outlined"
                margin="normal"
              />
              <TextField
                fullWidth
                label="Notification Message"
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                margin="normal"
              />

              <Typography variant="body2">Upload PDF</Typography>
              <Button variant="contained" component="label">
                Upload PDF
                <input type="file" hidden onChange={handleFileChange} />
              </Button>

              {/* Display file info and Clear button */}
              {file && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {uploadInfo}
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setFile(null);
                      setUploadInfo("");
                    }}
                  >
                    ❌ Clear File
                  </Button>
                </Box>
              )}

              <TextField
                fullWidth
                label="Deadline"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                variant="outlined"
                margin="normal"
              />
              <Button
                variant="contained"
                type="submit"
                color="primary"
                sx={{ mt: 2 }}
              >
                Submit Content
              </Button>
            </form>
          </Collapse>

          <Divider sx={{ my: 3 }} />
          <Typography variant="h6">Created Content</Typography>

          {contentList.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ mt: 1, fontStyle: "italic", color: "gray" }}
            >
              No content available. Please add content.
            </Typography>
          ) : (
            contentList.map((content) => (
              <Card key={content.id} sx={{ my: 1 }}>
                <CardContent
                  onClick={() =>
                    setExpandedContentId(
                      content.id === expandedContentId ? null : content.id
                    )
                  }
                  sx={{ cursor: "pointer" }}
                >
                  <Typography variant="subtitle1">
                    Content #{content.orderNumber}
                  </Typography>
                </CardContent>
                <Collapse in={expandedContentId === content.id}>
                  <CardContent>
                    <TextField
                      fullWidth
                      label="Exercise Link"
                      value={content.link}
                      disabled
                      variant="outlined"
                      margin="dense"
                    />
                    <TextField
                      fullWidth
                      label="Message"
                      value={content.message}
                      disabled
                      variant="outlined"
                      margin="dense"
                    />
                    <TextField
                      fullWidth
                      label="Deadline"
                      value={content.deadline}
                      disabled
                      variant="outlined"
                      margin="dense"
                    />
                    <TextField
                      fullWidth
                      label="File Path"
                      value={content.file}
                      disabled
                      variant="outlined"
                      margin="dense"
                    />
                  </CardContent>
                </Collapse>
              </Card>
            ))
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default Lesson;
